import { memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// === BINGO 90 CELL (Heavily Memoized) ===
interface Bingo90CellProps {
    number: number | null;
    isDaubed: boolean;
}

export const Bingo90Cell = memo(({ number, isDaubed }: Bingo90CellProps) => {
    const isEmpty = number === null;

    // State 1: Empty Cell
    if (isEmpty) {
        return (
            <div className="flex items-center justify-center bg-black/10 border border-white/5 rounded-[2px] sm:rounded-md shadow-inner aspect-[4/5] sm:aspect-square" />
        );
    }

    // State 2 & 3: Active vs Daubed
    return (
        <div className={`
            relative flex items-center justify-center 
            rounded-[2px] sm:rounded-md 
            transition-all duration-300
            aspect-[4/5] sm:aspect-square
            overflow-hidden select-none font-black
            ${isDaubed
                ? 'bg-gradient-to-br from-cyan-400 to-blue-600 text-white border border-white/30 shadow-[0_0_15px_rgba(56,189,248,0.5)] scale-105 z-10'  // Daubed Cyan/Blue
                : 'bg-black/20 text-white/70 border border-white/10 shadow-inner hover:bg-black/30' // Active Bright
            }
        `}>
            {/* Number Typography - Fluid text to prevent overflow on 360px mobile screens */}
            <span className={`
                font-black tracking-tighter relative z-20
                text-[clamp(14px,4.5vw,22px)] sm:text-[clamp(16px,2vw,24px)] 
                leading-none
                ${isDaubed ? 'text-[#0a0520] drop-shadow-md' : 'text-white drop-shadow-sm'}
            `}>
                {number}
            </span>

            {/* Animation Layer: Daub Pop */}
            <AnimatePresence>
                {isDaubed && (
                    <motion.div
                        initial={{ scale: 0, opacity: 0.8 }}
                        animate={{ scale: 1, opacity: 0 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        className="absolute inset-0 bg-white rounded-full mix-blend-overlay pointer-events-none z-10"
                    />
                )}
            </AnimatePresence>

            {/* Animation Layer: Daub Stamp Static Setting */}
            <AnimatePresence>
                {isDaubed && (
                    <motion.div
                        initial={{ scale: 0.2, opacity: 0, rotate: -15 }}
                        animate={{ scale: 1, opacity: 1, rotate: 0 }}
                        exit={{ scale: 0, opacity: 0, rotate: 15 }}
                        transition={{
                            type: "spring",
                            stiffness: 600,
                            damping: 18,
                            mass: 0.8
                        }}
                        className="absolute inset-0 border-[3px] border-white/60 pointer-events-none z-10 shadow-[inset_0_0_10px_rgba(255,255,255,0.5)]"
                    />
                )}
            </AnimatePresence>
        </div>
    );
}, (prevProps, nextProps) => {
    // CRITICAL: Only re-render this specific cell if its Daubed status changes.
    // The matrix number itself never mutates in Bingo 90.
    return prevProps.isDaubed === nextProps.isDaubed && prevProps.number === nextProps.number;
});

Bingo90Cell.displayName = 'Bingo90Cell';


// === BINGO 90 SINGLE CARD ===
interface Bingo90CardProps {
    grid: (number | null)[][];
    drawnNumbers: Set<number>;
    ticketId?: string;
}

export const Bingo90Card = memo(({ grid, drawnNumbers, ticketId }: Bingo90CardProps) => {
    // A standard Bingo 90 grid is always 3x9.
    const flatGrid = grid.flat();

    return (
        <div className="bg-gradient-to-b from-white/10 to-transparent backdrop-blur-xl border border-white/20 rounded-3xl p-3 sm:p-5 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.5)] flex flex-col gap-3 relative overflow-hidden">
            {/* Header */}
            {ticketId && (
                <div className="flex justify-between items-center px-1 mb-1">
                    <span className="text-xs font-black uppercase tracking-widest text-[#38BDF8] drop-shadow-md">
                        Ticket {ticketId.slice(0, 8)}
                    </span>
                    <span className="text-[10px] text-white/40 font-mono tracking-widest">
                        Bingo 90
                    </span>
                </div>
            )}

            {/* Matrix 9-Column Architecture Container */}
            <div className="grid grid-cols-9 gap-[1px] sm:gap-1 p-[1px] sm:p-1 bg-transparent rounded-lg">
                {flatGrid.map((number, idx) => {
                    const isDaubed = number !== null ? drawnNumbers.has(number) : false;
                    return (
                        <Bingo90Cell
                            key={idx}
                            number={number}
                            isDaubed={isDaubed}
                        />
                    );
                })}
            </div>
        </div>
    );
});

Bingo90Card.displayName = 'Bingo90Card';


// === BINGO 90 VERTICAL STRIP WRAPPER ===
// Stack multiple cards on top of each other.
interface Bingo90StripProps {
    tickets: { id: string; grid: (number | null)[][] }[];
    drawnNumbers: Set<number>;
}

export const Bingo90Strip = memo(({ tickets, drawnNumbers }: Bingo90StripProps) => {
    return (
        <div className="flex flex-col gap-3 sm:gap-4 w-full max-w-2xl mx-auto">
            {tickets.map((ticket) => (
                <Bingo90Card
                    key={ticket.id}
                    grid={ticket.grid}
                    drawnNumbers={drawnNumbers}
                    ticketId={ticket.id}
                />
            ))}
        </div>
    );
});

Bingo90Strip.displayName = 'Bingo90Strip';
